"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpanSubFormMutator = void 0;
const core_api_1 = require("@ikegps/core-api");
const id_1 = require("../../../../id");
const form_subform_span_1 = require("./form.subform.span");
const integrations_1 = require("../../../pole/integrations");
class SpanSubFormMutator {
    constructor(field) {
        this.field = field;
    }
    get state() {
        return this.field.getForm().state;
    }
    /**
     * Set the SubForm Type of this field to SPAN while linking the existing vector, span type,
     * and mid span photo fields.
     * Creates vector, type, and mid span photo fields if they don't exist.
     */
    enable() {
        let vectorField = this.field.fields.find(form_subform_span_1.isValidSpanVectorField);
        let typeField = this.field.fields.find(form_subform_span_1.isValidSpanTypeField);
        let midSpanPhotoField = this.field.fields.find(form_subform_span_1.isValidMidSpanIkePhotoField);
        if (!vectorField) {
            vectorField = this.addVectorField();
        }
        if (!typeField) {
            typeField = this.addSpanTypeField();
        }
        if (!midSpanPhotoField) {
            midSpanPhotoField = this.addMidSpanPhotoField();
        }
        this.setFieldReferences(vectorField.id, typeField.id, midSpanPhotoField.id);
    }
    /**
     * Disable SPAN subform type, unlocking all span fields and clearing state.
     */
    disable() {
        this.updateLockState(false);
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { subFormType: core_api_1.SubFormType.NONE, spanTypeState: undefined, locked: false }));
    }
    /**
     * Set the SubForm Type of this field to SPAN.
     *
     * @param vectorFieldId The Span Vector field ID to use.
     * @param typeFieldId The Span Type field ID to use.
     * @param midSpanIkePhotoFieldId Optional Mid Span IKE Photo field ID for two-way linking.
     * @throws If any of the given field IDs do not exist in this subform.
     */
    setFieldReferences(vectorFieldId, typeFieldId, midSpanIkePhotoFieldId) {
        if (!this.field.hasField(vectorFieldId)) {
            throw new Error(`Vector field '${vectorFieldId}' not found`);
        }
        if (!this.field.hasField(typeFieldId)) {
            throw new Error(`Span Type field '${typeFieldId}' not found`);
        }
        if (midSpanIkePhotoFieldId && !this.field.hasField(midSpanIkePhotoFieldId)) {
            throw new Error(`Mid span photo field '${midSpanIkePhotoFieldId}' not found`);
        }
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { subFormType: core_api_1.SubFormType.SPAN, spanTypeState: {
                vectorFieldId,
                typeFieldId,
                midSpanIkePhotoFieldId,
            }, locked: true }));
        this.updateLockState(true);
    }
    selectVectorField(fieldId) {
        this.updateFieldReference('vectorFieldId', fieldId);
    }
    selectSpanTypeField(fieldId) {
        this.updateFieldReference('typeFieldId', fieldId);
    }
    selectMidSpanIkePhotoField(fieldId) {
        this.updateFieldReference('midSpanIkePhotoFieldId', fieldId);
    }
    updateFieldReference(key, newFieldId) {
        const currentState = this.field.stateValue.spanTypeState;
        if (!currentState) {
            return;
        }
        const oldFieldId = currentState === null || currentState === void 0 ? void 0 : currentState[key];
        const newState = Object.assign(Object.assign({}, currentState), { [key]: newFieldId });
        if (newState.vectorFieldId && newState.typeFieldId && newFieldId) {
            this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { subFormType: core_api_1.SubFormType.SPAN, spanTypeState: newState, locked: true }));
        }
        const oldField = oldFieldId ? this.field.fields.find((f) => f.id === oldFieldId) : undefined;
        const newField = this.field.fields.find((f) => f.id === newFieldId);
        oldField === null || oldField === void 0 ? void 0 : oldField.Mutator.setLockState(false);
        newField === null || newField === void 0 ? void 0 : newField.Mutator.setLockState(true);
    }
    addMidSpanPhotoField() {
        return this.field.createField(core_api_1.IkeFieldType.TRUE_SIZE, {
            id: id_1.IkeId.generate(),
            displayName: 'Mid Span IKE Photo',
            locked: true,
        });
    }
    addVectorField() {
        return this.field.createField(core_api_1.IkeFieldType.VECTOR, {
            id: id_1.IkeId.generate(),
            displayName: 'Span Length',
            locked: true,
        });
    }
    addSpanTypeField() {
        const spanTypeField = this.field.createField(core_api_1.IkeFieldType.SELECT_LIST, {
            id: id_1.IkeId.generate(),
            displayName: 'Type',
            title: true,
            locked: true,
        });
        spanTypeField.Mutator.option({
            text: 'Fore Span',
            value: integrations_1.SpanType.NextPole,
            valueReadOnly: true,
            isDeletableOption: false,
        });
        spanTypeField.Mutator.option({
            text: 'Back Span',
            value: integrations_1.SpanType.PreviousPole,
            valueReadOnly: true,
            isDeletableOption: false,
        });
        spanTypeField.Mutator.option({
            text: 'Other',
            value: integrations_1.SpanType.Other,
            valueReadOnly: true,
            isDeletableOption: false,
        });
        spanTypeField.Mutator.option({
            text: 'Building',
            value: integrations_1.SpanType.Building,
            valueReadOnly: true,
            isDeletableOption: false,
        });
        return spanTypeField;
    }
    updateLockState(isLocked) {
        const vectorField = this.field.fields.find((field) => { var _a; return field.id === ((_a = this.field.subFormSpanTypeState) === null || _a === void 0 ? void 0 : _a.vectorFieldId); });
        const typeField = this.field.fields.find((field) => { var _a; return field.id === ((_a = this.field.subFormSpanTypeState) === null || _a === void 0 ? void 0 : _a.typeFieldId); });
        const midSpanIkePhotoField = this.field.fields.find((field) => { var _a; return field.id === ((_a = this.field.subFormSpanTypeState) === null || _a === void 0 ? void 0 : _a.midSpanIkePhotoFieldId); });
        vectorField === null || vectorField === void 0 ? void 0 : vectorField.Mutator.setLockState(isLocked);
        typeField === null || typeField === void 0 ? void 0 : typeField.Mutator.setLockState(isLocked);
        midSpanIkePhotoField === null || midSpanIkePhotoField === void 0 ? void 0 : midSpanIkePhotoField.Mutator.setLockState(isLocked);
    }
}
exports.SpanSubFormMutator = SpanSubFormMutator;
//# sourceMappingURL=form.subform.span.mutator.js.map